import { getItineraryDueDate } from './itinerary';
import moment from 'moment';

export const PAYMENT_TYPE = {
  BALANCE: 'balance',
  DEPOSIT: 'deposit',
  SELL: 'sell',
};

export const PAYMENT_STATUS = {
  ERROR: 'Error',
  AUTH: 'Authorized',
  PAID: 'Paid',
  PENDING: 'Pending',
};

export const PAYMENT_METHOD = {
  ACH: 'ach',
  BNPL: 'bnpl',
  CREDIT: 'credit',
  DIRECT_DEBIT: 'direct_debit',
  WIRE: 'wire',
};

export const PAYMENT_PROCESSOR = {
  STRIPE: 'Stripe',
  FLYWIRE: 'Flywire',
  UPLIFT: 'Uplift',
  WIRE: 'Wire',
};

export const DISCOUNT_ELIGIBLE_METHODS = [PAYMENT_METHOD.ACH, PAYMENT_METHOD.WIRE, PAYMENT_METHOD.DIRECT_DEBIT];

// Payment methods supported by the Payment.reversePayment endpoint. Reversal is
// processor-agnostic — the math is decided by paymentMethod, and the out-of-band
// refund (Stripe dashboard, Flywire portal, Uplift loan cancellation, bank reversal)
// is the SuperAdmin's responsibility.
export const REVERSIBLE_PAYMENT_METHODS = [
  PAYMENT_METHOD.CREDIT,
  PAYMENT_METHOD.ACH,
  PAYMENT_METHOD.DIRECT_DEBIT,
  PAYMENT_METHOD.BNPL,
  PAYMENT_METHOD.WIRE,
];

const reversiblePaymentMethodSet = new Set(REVERSIBLE_PAYMENT_METHODS);

export const isReversiblePayment = payment => {
  if (!payment) return false;
  return reversiblePaymentMethodSet.has(payment.paymentMethod);
};

export const DIRECT_DEBIT_TYPE = {
  EUR: 'SEPA',
  GBP: 'BACS',
  CAD: 'EFT',
  ZAR: 'EFT',
  USD: 'ACH',
};

export const DIRECT_DEBIT_LABEL = Object.fromEntries(
  Object.entries(DIRECT_DEBIT_TYPE).map(([currency, type]) => [currency, `Direct Debit (${type})`]),
);

export const ACH_DESCRIPTOR_LENGTH = 22;
export const CC_DESCRIPTOR_LENGTH = 15;

export const paymentMethodDisplay = paymentMethod => {
  switch (paymentMethod) {
    case PAYMENT_METHOD.ACH:
      return 'ACH';
    case PAYMENT_METHOD.CREDIT:
      return 'Credit Card';
    case PAYMENT_METHOD.WIRE:
      return 'Wire Transfer';
    case PAYMENT_METHOD.BNPL:
      return 'Pay Monthly';
    case PAYMENT_METHOD.DIRECT_DEBIT:
      return 'Direct Debit';
    default:
      return '';
  }
};

// Get the due date for an itinerary and paymentType
export const getPaymentDueDate = (itinerary, paymentType) =>
  paymentType === PAYMENT_TYPE.DEPOSIT ? moment().utc().startOf('day') : getItineraryDueDate(itinerary);

export const sanitizeDescriptor = (descriptor = '', maxLength) => {
  const sanitized = descriptor.replace(/['<>]/g, '').slice(0, maxLength);
  return sanitized || undefined;
};

const ITRVL = 'itrvl';

// Naming the institution and last4 inside the sentence keeps the authorization
// legible on its own, without depending on the account being rendered beside it.
const describeAccount = ({ bankName, last4 }) => {
  if (bankName && last4) return `your ${bankName} account ending in ${last4}`;
  if (last4) return `your bank account ending in ${last4}`;
  if (bankName) return `your ${bankName} account`;
  return 'the bank account above';
};

// itrvl holds the Stripe account that initiates the debit, so it is the originator
// Nacha requires the mandate to name. The agency is the merchant the payer bought from
// and the party they serve notice on, and appears on the bank statement descriptor.
const describeOriginator = agencyName => (agencyName === ITRVL ? ITRVL : `${ITRVL} (the payment partner of ${agencyName})`);

const authorizationTerms = ({ agencyName, bankName, last4 }) =>
  `${describeOriginator(agencyName)} to debit ${describeAccount({ bankName, last4 })} for any amount owed for charges arising from your use of ${agencyName}'s services and/or purchase of products from ${agencyName}, pursuant to ${agencyName}'s website and terms, until this authorization is revoked.`;

const revocationTerms = agencyName => `this authorization at any time by providing notice to ${agencyName} with 30 (thirty) days notice.`;

// The payer's browser shows this text and the API re-derives it when recording the
// payment, so both sides MUST resolve agencyName from the itinerary's agency and apply
// the same fallback here — a divergence would record wording the payer never saw.
const mandateAgencyName = agencyName => agencyName || ITRVL;

// Nacha requires the payer to affirmatively accept debit authorization terms before
// the debit is initiated. The agency is the named originator.
export const achMandatePrompt = ({ agencyName, bankName, last4 }) => {
  const name = mandateAgencyName(agencyName);
  return `By checking this box, you authorize ${authorizationTerms({ agencyName: name, bankName, last4 })} You may amend or cancel ${revocationTerms(name)}`;
};

// Restates the accepted authorization once the payment method section collapses, so
// the terms stay on screen through checkout rather than only at the moment of consent.
export const achMandateRecord = ({ agencyName, bankName, last4 }) => {
  const name = mandateAgencyName(agencyName);
  return `You have authorized ${authorizationTerms({ agencyName: name, bankName, last4 })} You can amend or cancel ${revocationTerms(name)}`;
};

export const paymentDefaultName = (itinerary, payment) => payment.name ?? [itinerary.name, payment.paymentType].filter(Boolean).join(' ');
